import { Alert, Badge, Button, EmptyState, Input, Skeleton } from "@aman/design-system";
import { type FormEvent, type ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ApiError } from "../api/client";
import type { AnyRecord } from "../types";
import { stringValue } from "../types";

export function ApiErrorAlert({ error, onRetry }: { error: ApiError | undefined; onRetry?: () => void }) {
  const { t } = useTranslation();
  if (error === undefined) return null;
  const translated = t(`errors.${error.code}`, { defaultValue: t("errors.generic") });
  return (
    <Alert tone="error" title={t("errors.title")}>
      <div>{translated}</div>
      <div className="error-metadata"><code>{t("common.apiCode", { code: error.code })}</code>{error.requestId !== undefined ? <code>{t("common.requestId", { id: error.requestId })}</code> : null}</div>
      {onRetry !== undefined ? <Button type="button" variant="secondary" size="small" onClick={onRetry}>{t("common.retry")}</Button> : null}
    </Alert>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return <header className="page-header"><div><h1>{title}</h1>{subtitle !== undefined ? <p>{subtitle}</p> : null}</div>{actions !== undefined ? <div className="page-header__actions">{actions}</div> : null}</header>;
}

export function LoadingTable({ rows = 5, columns = 5 }: { rows?: number; columns?: number }) {
  return <div className="loading-table">{Array.from({ length: rows }, (_, row) => <div className="loading-table__row" key={row}>{Array.from({ length: columns }, (_, column) => <Skeleton key={column} />)}</div>)}</div>;
}

export interface Column {
  key: string;
  label: string;
  render?: (record: AnyRecord) => ReactNode;
}

function fallbackValue(value: unknown): ReactNode {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "✓" : "—";
  if (["string", "number", "bigint"].includes(typeof value)) return String(value);
  return <code className="compact-json">{JSON.stringify(value)}</code>;
}

export function statusTone(status: unknown): "neutral" | "success" | "warning" | "danger" | "info" {
  const normalized = stringValue(status).toLowerCase();
  if (["active", "enabled", "healthy", "completed", "paid", "delivered", "success", "succeeded", "not_required", "installed"].includes(normalized)) return "success";
  if (["failed", "blocked", "revoked", "unhealthy", "expired", "dead", "cancelled"].includes(normalized)) return "danger";
  if (["pending", "suspended", "reserved", "running", "unknown", "degraded"].includes(normalized)) return "warning";
  return "neutral";
}

export function DataTable({ rows, columns, rowKey, actions, emptyAction }: { rows: AnyRecord[]; columns: Column[]; rowKey?: (record: AnyRecord, index: number) => string; actions?: (record: AnyRecord) => ReactNode; emptyAction?: ReactNode }) {
  const { t } = useTranslation();
  if (rows.length === 0) return <EmptyState title={t("common.noData")} description={t("common.noDataDescription")} action={emptyAction} />;
  return (
    <div className="table-scroll">
      <table className="data-table">
        <thead><tr>{columns.map((column) => <th key={column.key}>{column.label}</th>)}{actions !== undefined ? <th>{t("common.actions")}</th> : null}</tr></thead>
        <tbody>{rows.map((record, index) => <tr key={rowKey?.(record, index) ?? stringValue(record["id"], String(index))}>{columns.map((column) => <td key={column.key}>{column.render?.(record) ?? fallbackValue(record[column.key])}</td>)}{actions !== undefined ? <td><div className="table-actions">{actions(record)}</div></td> : null}</tr>)}</tbody>
      </table>
    </div>
  );
}

export function StatusBadge({ value }: { value: unknown }) {
  const { t } = useTranslation();
  const status = stringValue(value, "unknown");
  return <Badge tone={statusTone(status)}>{t(`common.${status}`, { defaultValue: status })}</Badge>;
}

export function SearchBar({ value, onChange, onRefresh, loading, extra }: { value: string; onChange: (value: string) => void; onRefresh: () => void; loading: boolean; extra?: ReactNode }) {
  const { t } = useTranslation();
  return <div className="toolbar"><div className="search-box"><span aria-hidden="true">⌕</span><Input value={value} onChange={(event) => onChange(event.target.value)} placeholder={t("common.search")} aria-label={t("common.search")} /></div><div className="toolbar__actions">{extra}<Button type="button" variant="secondary" size="small" busy={loading} onClick={onRefresh}>↻ {t("common.refresh")}</Button></div></div>;
}

export function useFilteredRows(rows: AnyRecord[], search: string): AnyRecord[] {
  return useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    if (query === "") return rows;
    return rows.filter((row) => Object.values(row).some((value) => typeof value === "string" && value.toLocaleLowerCase().includes(query)));
  }, [rows, search]);
}

export interface SimpleFormValue { [key: string]: string | boolean }
export interface SimpleField {
  key: string;
  label: string;
  type?: "text" | "email" | "password" | "number" | "date" | "checkbox" | "textarea" | "select";
  required?: boolean;
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
  hint?: string | undefined;
  pattern?: string;
  min?: number;
  max?: number;
  step?: number;
  dir?: "ltr" | "rtl" | "auto";
}

export function SimpleForm({ fields, initial, busy, submitLabel, onSubmit }: { fields: SimpleField[]; initial?: SimpleFormValue; busy?: boolean; submitLabel: string; onSubmit: (value: SimpleFormValue) => Promise<void> | void }) {
  const { t } = useTranslation();
  const [value, setValue] = useState<SimpleFormValue>(initial ?? {});
  const submit = (event: FormEvent): void => { event.preventDefault(); void onSubmit(value); };
  return <form className="form-grid" onSubmit={submit}>{fields.map((field) => {
    const inputValue = value[field.key];
    if (field.type === "checkbox") return <label className="check-field" key={field.key}><input type="checkbox" checked={inputValue === true} onChange={(event) => setValue((current) => ({ ...current, [field.key]: event.target.checked }))} /><span>{field.label}</span></label>;
    return <label className="aman-field" key={field.key}><span className="aman-label">{field.label}</span>{field.type === "textarea" ? <textarea className="aman-textarea" required={field.required} value={typeof inputValue === "string" ? inputValue : ""} placeholder={field.placeholder} dir={field.dir} onChange={(event) => setValue((current) => ({ ...current, [field.key]: event.target.value }))} /> : field.type === "select" ? <select className="aman-select" required={field.required} value={typeof inputValue === "string" ? inputValue : ""} onChange={(event) => setValue((current) => ({ ...current, [field.key]: event.target.value }))}><option value="">{t("common.select")}</option>{field.options?.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}</select> : <input className="aman-input" type={field.type ?? "text"} required={field.required} value={typeof inputValue === "string" ? inputValue : ""} placeholder={field.placeholder} pattern={field.pattern} min={field.min} max={field.max} step={field.step} dir={field.dir} onChange={(event) => setValue((current) => ({ ...current, [field.key]: event.target.value }))} />}{field.hint !== undefined ? <span className="aman-hint">{field.hint}</span> : null}</label>;
  })}<div className="form-actions"><Button type="submit" {...(busy === undefined ? {} : { busy })}>{submitLabel}</Button></div></form>;
}
